import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface FractionState {
  numerator1: string;
  denominator1: string;
  numerator2: string;
  denominator2: string;
  operation: '+' | '-' | '×' | '÷';
}

interface FractionResult {
  numerator: number;
  denominator: number;
  decimal: number;
  simplified: {
    numerator: number;
    denominator: number;
  };
}

function KalkulatorRazlomaka() {
  const [values, setValues] = useState<FractionState>({
    numerator1: '',
    denominator1: '',
    numerator2: '',
    denominator2: '',
    operation: '+'
  });

  const [result, setResult] = useState<FractionResult | null>(null);

  const gcd = (a: number, b: number): number => {
    return b === 0 ? Math.abs(a) : gcd(b, a % b);
  };

  const simplifyFraction = (numerator: number, denominator: number) => {
    const divisor = gcd(numerator, denominator);
    return {
      numerator: numerator / divisor,
      denominator: denominator / divisor
    };
  };

  const calculateFraction = () => {
    const n1 = parseInt(values.numerator1);
    const d1 = parseInt(values.denominator1);
    const n2 = parseInt(values.numerator2);
    const d2 = parseInt(values.denominator2);

    if (isNaN(n1) || isNaN(d1) || isNaN(n2) || isNaN(d2) || d1 === 0 || d2 === 0) {
      return null;
    }

    let resultNumerator: number;
    let resultDenominator: number;

    switch (values.operation) {
      case '+':
        resultNumerator = n1 * d2 + n2 * d1;
        resultDenominator = d1 * d2;
        break;
      case '-':
        resultNumerator = n1 * d2 - n2 * d1;
        resultDenominator = d1 * d2;
        break;
      case '×':
        resultNumerator = n1 * n2;
        resultDenominator = d1 * d2;
        break;
      case '÷':
        if (n2 === 0) return null;
        resultNumerator = n1 * d2;
        resultDenominator = d1 * n2;
        break;
      default:
        return null;
    }

    const simplified = simplifyFraction(resultNumerator, resultDenominator);
    
    // Ensure positive denominator
    if (simplified.denominator < 0) {
      simplified.numerator *= -1;
      simplified.denominator *= -1;
    }

    return {
      numerator: resultNumerator,
      denominator: resultDenominator,
      decimal: resultNumerator / resultDenominator,
      simplified
    };
  };

  const handleCalculate = () => {
    const result = calculateFraction();
    setResult(result);
  };

  const handleInputChange = (field: keyof FractionState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      result: null
    }));
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Razlomaka",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte operacije s razlomcima. Zbrajanje, oduzimanje, množenje i dijeljenje razlomaka s automatskim skraćivanjem.",
    "url": "https://online-kalkulator.com/kalkulator-razlomaka",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Razlomaka | Izračun Operacija s Razlomcima"
        description="Izračunajte operacije s razlomcima. Zbrajanje, oduzimanje, množenje i dijeljenje razlomaka s automatskim skraćivanjem."
        canonicalUrl="/kalkulator-razlomaka"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Razlomaka</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-5 gap-4 items-center">
                <div className="col-span-2">
                  <input
                    type="number"
                    value={values.numerator1}
                    onChange={(e) => handleInputChange('numerator1', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brojnik"
                  />
                  <div className="border-t-2 border-gray-800 my-1"></div>
                  <input
                    type="number"
                    value={values.denominator1}
                    onChange={(e) => handleInputChange('denominator1', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nazivnik"
                  />
                </div>

                <div className="flex justify-center">
                  <select
                    value={values.operation}
                    onChange={(e) => handleInputChange('operation', e.target.value as FractionState['operation'])}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="+">+</option>
                    <option value="-">−</option>
                    <option value="×">×</option>
                    <option value="÷">÷</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <input
                    type="number"
                    value={values.numerator2}
                    onChange={(e) => handleInputChange('numerator2', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brojnik"
                  />
                  <div className="border-t-2 border-gray-800 my-1"></div>
                  <input
                    type="number"
                    value={values.denominator2}
                    onChange={(e) => handleInputChange('denominator2', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nazivnik"
                  />
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {result && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center">
                    <div className="inline-block">
                      <div className="text-2xl font-bold text-blue-600">
                        {result.simplified.numerator}
                      </div>
                      <div className="border-t-2 border-gray-800 my-1"></div>
                      <div className="text-2xl font-bold text-blue-600">
                        {result.simplified.denominator}
                      </div>
                    </div>
                  </div>

                  <div className="text-center text-gray-600">
                    = {result.decimal.toFixed(4)}
                  </div>

                  {(result.numerator !== result.simplified.numerator || 
                    result.denominator !== result.simplified.denominator) && (
                    <div className="text-center text-sm text-gray-500">
                      Skraćeni oblik razlomka {result.numerator}/{result.denominator}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Razlomaka</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator razlomaka omogućuje vam jednostavno izvođenje osnovnih matematičkih
                  operacija s razlomcima. Automatski skraćuje rezultate i prikazuje decimalni
                  ekvivalent za lakše razumijevanje.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Operacije s Razlomcima</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Zbrajanje razlomaka</h3>
                    <p className="text-gray-600">
                      a/b + c/d = (ad + bc)/bd
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Oduzimanje razlomaka</h3>
                    <p className="text-gray-600">
                      a/b - c/d = (ad - bc)/bd
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Množenje razlomaka</h3>
                    <p className="text-gray-600">
                      a/b × c/d = ac/bd
                    </p>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Dijeljenje razlomaka</h3>
                    <p className="text-gray-600">
                      a/b ÷ c/d = ad/bc
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite brojnik i nazivnik prvog razlomka</li>
                  <li>Odaberite željenu operaciju (+, -, ×, ÷)</li>
                  <li>Unesite brojnik i nazivnik drugog razlomka</li>
                  <li>Kliknite "Izračunaj" za rezultat</li>
                  <li>Rezultat će biti prikazan u skraćenom obliku i kao decimalni broj</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Skraćivanje Razlomaka</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Razlomak se skraćuje dijeljenjem brojnika i nazivnika njihovim najvećim zajedničkim djeliteljem</li>
                  <li>Kalkulator automatski pronalazi najveći zajednički djelitelj</li>
                  <li>Rezultat je uvijek prikazan u najjednostavnijem obliku</li>
                  <li>Negativni razlomci su prikazani s negativnim brojnikom</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Nazivnik ne može biti nula</li>
                  <li>Kod dijeljenja, drugi razlomak ne smije biti nula</li>
                  <li>Rezultati su prikazani s 4 decimalna mjesta</li>
                  <li>Negativne brojeve unosite s predznakom minus (-)</li>
                  <li>Razlomci su uvijek prikazani s pozitivnim nazivnikom</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorRazlomaka;